import type { Design_ } from 'src/api/db/apiDesigns'
import type { QuoteItem_ } from 'src/api/db/apiQuoteItem'
import type { ProductVariantColor_ } from 'src/api/db/apiProductVariantColor'
import {
  getQuoteItemThumbnailUrl,
  resolveQuoteItemVariantColor,
} from 'src/shared/utils/quoteItemThumbnail'

export type GalleryDesignProductPreview = {
  product_id: number
  chosen_product_variant_color: ProductVariantColor_
  product_variant_colors: ProductVariantColor_[]
  product_name: string
  product_description: string
  moq: number
  article_number: string
  price: number
  klayder_recommends: boolean
  klayder_recommendation_text: string
  product_categories?: Array<{ id: number; category?: { title?: string } }>
}

export type GalleryDesignRow = {
  design: Design_ | undefined
  productName: string
  decorationName: string
  quoteItemId: number
  quoteSubItemId: number
  quoteId?: number | null
  price: number
  fallbackImage?: string | null
  mockup_urls?: string[] | null
  productPreview: GalleryDesignProductPreview | null
}

/** Decoration rows for canvas / DecorationCard (quote line × decoration sub-items). */
export const mapQuoteItemsToDesignRows = (
  quoteItems: QuoteItem_[] | undefined,
  t: (en: string, sv?: string) => string
): GalleryDesignRow[] | undefined => {
  if (!quoteItems) return undefined
  return quoteItems.flatMap((quoteItem) => {
    const product = quoteItem.product
    const { chosen_product_variant_color, product_variant_colors } =
      resolveQuoteItemVariantColor(quoteItem)

    const productPreview =
      product?.id && chosen_product_variant_color
        ? {
            product_id: product.id,
            chosen_product_variant_color,
            product_variant_colors,
            product_name: product.item_name || t('Product', 'Produkt'),
            product_description: product.item_description || '',
            moq: product.moq || 1,
            article_number: product.article_number || '',
            price: product.price || 0,
            klayder_recommends: product.klayder_recommends || false,
            klayder_recommendation_text:
              product.klayder_recommendation_text || '',
            product_categories: product.category_product,
          }
        : null

    return (quoteItem.quote_sub_item ?? [])
      .filter((subItem) => subItem.type === 'decoration')
      .map((subItem) => ({
        design: subItem.design?.[0],
        productName: product?.item_name || t('Product', 'Produkt'),
        decorationName: subItem.description,
        quoteItemId: quoteItem.id,
        quoteSubItemId: subItem.id,
        quoteId: quoteItem.quote_id,
        price: product?.price || 0,
        fallbackImage: getQuoteItemThumbnailUrl(quoteItem),
        mockup_urls: subItem.mockup_urls ?? null,
        productPreview,
      }))
  })
}

/** Share-order decoration list (quote line × design sub-items). */
export const extractAllDecorations = (
  quoteItems: QuoteItem_[] | undefined,
  t: (en: string, sv?: string) => string
) => {
  if (!quoteItems) return []
  return quoteItems.flatMap((quoteItem) => {
    const productName = quoteItem.product?.item_name || t('Product', 'Produkt')
    return (quoteItem.quote_sub_item ?? []).flatMap((subItem) =>
      (subItem.design ?? []).map((design: Design_) => ({
        design,
        productName,
        quoteItemId: quoteItem.id,
      }))
    )
  })
}
